import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  GlobalMessageService,
  GlobalMessageType,
  OCC_CART_ID_CURRENT,
  TranslationService,
  User,
  UserIdService
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { filter, take, tap, withLatestFrom } from 'rxjs/operators';
import { DirectInputForm } from '../uig-cart.models';
import { UigCartService } from '../uig-cart.service';
import {ActiveCartService, getCartIdByUserId, MultiCartService} from "@spartacus/cart/base/core";

@Component({
    selector: 'app-uig-fast-entry',
    templateUrl: './uig-fast-entry.component.html',
    styleUrls: ['./uig-fast-entry.component.scss'],
    standalone: false
})
export class UigFastEntryComponent implements OnInit, OnDestroy {
  fastEntryForm: UntypedFormGroup;
  addFastEntry$: Observable<Object>;
  isRestricted: boolean = false;
  public isLoading$ = new BehaviorSubject(false);
  RIO_CART_ORDER: string = "rio_cart_order";
  RIO_CART_CREATE_UPDATE: string = "rio_cart_create_update";
  roles: string[];
  private userSubscription = new Subscription();
  SHOW_MSG_TIMEOUT_CONFIRMATION: number = 600000;

  constructor(
    private uigCartService: UigCartService,
    private formBuilder: UntypedFormBuilder,
    private activeCartService: ActiveCartService,
    private translation: TranslationService,
    private multiCartService: MultiCartService,
    private translationService: TranslationService,
    private globalMessageService: GlobalMessageService,
    private userIdService: UserIdService,
    private userAccountFacade: UserAccountFacade,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.fastEntryForm = this.formBuilder.group({
      entries: this.formBuilder.array([
        this.addFormGroup(),
        this.addFormGroup(),
      ]),
    });
  }
  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  ngOnInit(): void {
    this.checkForRoles();
  }

  onSubmit() {
    var error = false;
    this.isLoading$.next(true);
    var fastEntryData = {} as DirectInputForm;
    fastEntryData.entries = this.fillEntries();

    this.activeCartService
      .requireLoadedCart()
      .pipe(withLatestFrom(this.userIdService.getUserId()))
      .subscribe(([cartState, userId]) => {
        this.addFastEntry$ = this.uigCartService.addFastEntry(
          fastEntryData,
          getCartIdByUserId(cartState, userId)
        );
        this.addFastEntry$
          .pipe(
            tap((productsWithError) => {
              if (Object.keys(productsWithError).length > 0) {
                error = true;
                this.multiCartService.reloadCart(OCC_CART_ID_CURRENT);
                this.showErrorMessages(productsWithError);
              }
            })
          )
          .subscribe((_res) => {
            if (!error) {
              this.multiCartService.reloadCart(OCC_CART_ID_CURRENT);
              this.fastEntryForm = this.formBuilder.group({
                entries: this.formBuilder.array([
                  this.addFormGroup(),
                  this.addFormGroup(),
                ]),
              });
              this.changeDetectorRef.detectChanges();
              this.translationService
              .translate('fastEntry.addEntryConfirmation')
              .pipe(take(1))
              .subscribe((message) => {
                this.globalMessageService.add(
                  message,
                  GlobalMessageType.MSG_TYPE_CONFIRMATION,
                  this.SHOW_MSG_TIMEOUT_CONFIRMATION
                  );
                  setTimeout(() => {
                    this.globalMessageService.remove(
                      GlobalMessageType.MSG_TYPE_CONFIRMATION
                      );
                    }, this.SHOW_MSG_TIMEOUT_CONFIRMATION);
                  });
                }
                this.isLoading$.next(false);
              });
      });
  }

  addFormGroup(code?, quantity?, note?): UntypedFormGroup {
    return this.formBuilder.group({
      code: [code ? code : ''],
      quantity: [quantity ? quantity : ''],
      note: [note ? note : ''],
    });
  }

  addOnChange(index: Number) {
    if (this.getEntries().length - 1 == index) {
      this.getEntries().push(this.addFormGroup());
    }
  }

  getEntries(): UntypedFormArray {
    return this.fastEntryForm.get('entries') as UntypedFormArray;
  }

  showErrorMessages(errorMessages: any) {
    let failedCode = new Set();
    for (let errorMsg of errorMessages) {
      if (errorMsg.message === 'notesMaxLength') {
        failedCode.add(errorMsg.articleNo);
        this.translation
          .translate('fastEntry.errors.notesMaxLength', {
            productCode: errorMsg.articleNo,
          })
          .pipe(take(1))
          .subscribe((message) => {
            this.globalMessageService.add(message, GlobalMessageType.MSG_TYPE_ERROR, this.SHOW_MSG_TIMEOUT_CONFIRMATION);
          });
      } else if (errorMsg.message === 'invalidCode') {
        failedCode.add(errorMsg.articleNo);
        this.translation
          .translate('fastEntry.errors.invalidCode', {
            productCode: errorMsg.articleNo,
          })
          .pipe(take(1))
          .subscribe((message) => {
            this.globalMessageService.add(message, GlobalMessageType.MSG_TYPE_ERROR, this.SHOW_MSG_TIMEOUT_CONFIRMATION);
          });
      } else if (errorMsg.message === 'zeroQty') {
        failedCode.add(errorMsg.articleNo);
        this.translation
          .translate('fastEntry.errors.zeroQty', {
            productCode: errorMsg.articleNo,
          })
          .pipe(take(1))
          .subscribe((message) => {
            this.globalMessageService.add(
              message,
              GlobalMessageType.MSG_TYPE_WARNING,
              this.SHOW_MSG_TIMEOUT_CONFIRMATION
            );
          });
      } else if (errorMsg.message === 'notListed') {
        failedCode.add(errorMsg.articleNo);
        this.translation
          .translate('fastEntry.errors.notListed', {
            productCode: errorMsg.articleNo,
          })
          .pipe(take(1))
          .subscribe((message) => {
            this.globalMessageService.add(message, GlobalMessageType.MSG_TYPE_ERROR, this.SHOW_MSG_TIMEOUT_CONFIRMATION);
          });
      } else if (errorMsg.message === 'backendMessage') {
        this.translation
          .translate('uigAddToCart.backendMessage', {
            productCode: errorMsg.articleNo,
            errDescription: errorMsg.translatedMessage,
          })
          .pipe(take(1))
          .subscribe((message) => {
            this.globalMessageService.add(
              message,
              GlobalMessageType.MSG_TYPE_WARNING,
              this.SHOW_MSG_TIMEOUT_CONFIRMATION
            );
          });
      } else {
        failedCode.add(errorMsg.articleNo);
        this.globalMessageService.add(errorMsg, GlobalMessageType.MSG_TYPE_ERROR, this.SHOW_MSG_TIMEOUT_CONFIRMATION);
      }
    }
    this.updateFormEntries(failedCode);
  }

  fillEntries(): Array<any> {
    var entries = new Array();
    for (let entry of this.fastEntryForm.get('entries')['controls']) {
      let { code, quantity, note } = this.constructEntry(entry);
      entries.push({ code, quantity, note });
    }
    return entries;
  }

  updateFormEntries(failedCode) {
    var failedEntries = new Array();
    for (let entry of this.fastEntryForm.get('entries')['controls']) {
      let code = entry.value['code'];
      if (failedCode.has(code)) {
        let { code, quantity, note } = this.constructEntry(entry);
        failedEntries.push({ code, quantity, note });
      }
    }
    this.fastEntryForm = this.formBuilder.group({
      entries: this.formBuilder.array([
        this.addFormGroup(),
      ]),
    });
    for (let failedEntry of failedEntries) {
      this.getEntries().push(
        this.addFormGroup(
          failedEntry.code,
          failedEntry.quantity,
          failedEntry.note
        )
      );
    }
    this.getEntries().push(this.addFormGroup());
    this.changeDetectorRef.detectChanges();
  }

  checkForRoles() {
    this.userSubscription = this.userAccountFacade
      .get()
      .pipe(filter((user: User) => user && Object.keys(user).length > 0))
      .subscribe((user) => {
        const hasRole =
          user.roles && (user.roles.includes(this.RIO_CART_CREATE_UPDATE)
            || user.roles.includes(this.RIO_CART_ORDER));
        if (!hasRole) {
          this.isRestricted = true;
        }
      });
  }
  private constructEntry(entry: any) {
    let code = entry.value['code'];
    let quantity;
    if (!entry.value['quantity']) {
      quantity = 0;
    } else {
      quantity = entry.value['quantity'];
    }
    let note = entry.value['note'];
    return { code, quantity, note };
  }
}
